import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { opportunityService } from '../../services/opportunity.service';
import { operatorService } from '../../services/operator.service';
import { contactService } from '../../services/contact.service';
import type {
  Contact,
  Operator,
  Opportunity,
  OpportunityInput,
  OpportunityStage,
} from '../../types/crm';
import Loading from '../../components/Loading';

const STAGES: OpportunityStage[] = ['planning', 'in_process', 'won', 'lost'];

const createOpportunitySchema = z.object({
  title: z.string().min(1, 'Title is required'),
  operator_id: z.string().min(1, 'Operator is required'),
  contact_id: z.string().optional(),
  stage: z.enum(['planning', 'in_process', 'won', 'lost']),
  annual_cases: z.string().optional(),
  expected_close_date: z.string().optional(),
  notes: z.string().optional(),
});

type CreateOpportunityFormData = z.infer<typeof createOpportunitySchema>;

const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const stageBadge = (stage: OpportunityStage): string => {
  switch (stage) {
    case 'won':
      return 'bg-green-100 text-green-800';
    case 'in_process':
      return 'bg-blue-100 text-blue-800';
    case 'lost':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

export const Opportunities = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<OpportunityStage | ''>('');
  const [operatorFilter, setOperatorFilter] = useState<number | ''>('');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateOpportunityFormData>({
    resolver: zodResolver(createOpportunitySchema),
    defaultValues: {
      stage: 'planning',
    },
  });

  const selectedOperatorId = watch('operator_id');

  const loadOpportunities = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (search) params['q[title_cont]'] = search;
      if (stageFilter) params['q[stage_eq]'] = stageFilter;
      if (operatorFilter) params['q[operator_id_eq]'] = operatorFilter;
      const result = await opportunityService.getAll(params);
      setOpportunities(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [search, stageFilter, operatorFilter]);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  useEffect(() => {
    operatorService
      .getAll({ per_page: 200 })
      .then((res) => setOperators(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  // Refresh the contact list when the modal operator selection changes.
  useEffect(() => {
    if (!selectedOperatorId) {
      setContacts([]);
      return;
    }
    contactService
      .getAll({ 'q[operator_id_eq]': selectedOperatorId, per_page: 100 })
      .then((res) => setContacts(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, [selectedOperatorId]);

  const onCreate = async (data: CreateOpportunityFormData) => {
    setIsCreating(true);
    try {
      const input: OpportunityInput = {
        operator_id: parseInt(data.operator_id, 10),
        contact_id: data.contact_id ? parseInt(data.contact_id, 10) : null,
        title: data.title,
        stage: data.stage,
        annual_cases: data.annual_cases?.trim() ? data.annual_cases.trim() : null,
        expected_close_date: data.expected_close_date?.trim() ? data.expected_close_date.trim() : null,
        notes: data.notes?.trim() ? data.notes.trim() : null,
      };
      await opportunityService.create(input);
      toast.success('Opportunity created');
      setShowCreateModal(false);
      reset();
      loadOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Opportunities</h1>
          <p className="text-sm text-gray-600 mt-1">Deals in flight with operators in your account.</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Opportunity
        </button>
      </div>

      <div className="card p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="label">Search</label>
          <input
            type="text"
            placeholder="Search by title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label">Stage</label>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as OpportunityStage | '')}
            className="input"
          >
            <option value="">All stages</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>{titleize(s)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Operator</label>
          <select
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value ? Number(e.target.value) : '')}
            className="input"
          >
            <option value="">All operators</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>{op.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8"><Loading /></div>
        ) : opportunities.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No opportunities found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Annual Cases</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Close</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {opportunities.map((opp) => (
                  <tr key={opp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                      <Link to={`/crm/opportunities/${opp.id}`}>{opp.title}</Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{opp.operator.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stageBadge(opp.stage)}`}>
                        {titleize(opp.stage)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">{opp.annual_cases || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{opp.expected_close_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Opportunity</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="title" className="label">Title</label>
                      <input {...register('title')} id="title" type="text" className="input" />
                      {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="operator_id" className="label">Operator</label>
                      <select {...register('operator_id')} id="operator_id" className="input">
                        <option value="">Select an operator…</option>
                        {operators.map((op) => (
                          <option key={op.id} value={op.id}>{op.name}</option>
                        ))}
                      </select>
                      {errors.operator_id && <p className="mt-1 text-sm text-red-600">{errors.operator_id.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="contact_id" className="label">Primary contact (optional)</label>
                      <select
                        {...register('contact_id')}
                        id="contact_id"
                        className="input"
                        disabled={!selectedOperatorId}
                      >
                        <option value="">— none —</option>
                        {contacts.map((c) => (
                          <option key={c.id} value={c.id}>{c.full_name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="stage" className="label">Stage</label>
                        <select {...register('stage')} id="stage" className="input">
                          {STAGES.map((s) => (
                            <option key={s} value={s}>{titleize(s)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="annual_cases" className="label">Annual cases</label>
                        <input {...register('annual_cases')} id="annual_cases" type="text" inputMode="decimal" className="input" />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="expected_close_date" className="label">Expected close date</label>
                      <input {...register('expected_close_date')} id="expected_close_date" type="date" className="input" />
                    </div>

                    <div>
                      <label htmlFor="notes" className="label">Notes</label>
                      <textarea {...register('notes')} id="notes" rows={3} className="input" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Creating...' : 'Create Opportunity'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Opportunities;
